<template>
  <TransitionRoot as="template" :show="open" class="z-50">
    <Dialog
      as="div"
      class="relative z-10"
      @close="keepModalOpen ? '' : $emit('close-modal')"
      id="main-modal"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 backdrop-blur-[2px] bg-[rgba(20,17,11,0.8)]" />
      </TransitionChild>
      <div class="fixed inset-0 z-10">
        <div class="flex h-full items-end text-center lg:items-center lg:justify-center">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel id="modal" :class="inputStyles" class="flex flex-col">
              <div
                v-if="!hideHeader"
                id="header"
                class="flex w-full items-center justify-between bg-bg-canvas border-b border-outline-dividers p-4"
              >
                <div>
                  <slot name="modal-header">
                    <span v-if="headerTitle" class="text-lg font-medium text-neutral-gray-12">{{
                      headerTitle
                    }}</span>
                  </slot>
                </div>

                <Button
                  v-if="!UIStore.badTokenModalOpen && !hideClose"
                  @click="$emit('close-modal')"
                  variant="paper-primary"
                  size="sm"
                >
                  <template #icon>
                    <Icon name="X" class="h-4 w-4 text-cta-tertiary-outline-icon" />
                  </template>
                </Button>
              </div>
              <div class="grow overflow-auto bg-bg-canvas">
                <slot name="modal-body">
                  <div class="flex w-full h-full justify-center items-center">
                    <p class="p-6 text-light">No modal body content provided.</p>
                  </div>
                </slot>
              </div>
              <div
                v-if="!hideFooter"
                id="footer"
                class="relative w-full border-t border-outline-dividers bg-bg-canvas px-6 py-value-2"
              >
                <slot name="modal-footer">
                  <div class="w-full flex justify-end">
                    <Button
                      @click="$emit('close-modal')"
                      variant="paper-primary"
                      size="md"
                      class=""
                    >
                      <template #label>Cancel</template>
                    </Button>
                  </div>
                </slot>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, watch } from "vue";
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { useUIStore } from "@/stores/useUIStore";
const UIStore = useUIStore();
import { Button, Icon } from "@/components";

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  customHeight: {
    type: String,
    default: " h-[85%] lg:h-156 ",
  },
  customWidth: {
    type: String,
    default: "lg:w-250",
  },
  size: {
    type: String,
    default: "default",
  },
  headerTitle: {
    type: String,
    default: "Modal",
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
  keepModalOpen: {
    type: Boolean,
    default: false,
  },
  hideClose: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close-modal"]);

watch(
  () => props.open,
  (newVal) => {
    document.body.style.overflow = newVal ? "hidden" : "auto";
  }
);

const inputStyles = computed(() => {
  let baseClass = `relative w-full transform overflow-hidden rounded-t-lg lg:rounded-lg bg-neutral-gray-1 text-left shadow-xl transition-all `;
  return `${baseClass} ${props.customHeight} ${props.customWidth}`;
});
</script>
